import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pin, RefreshCw } from 'lucide-react';
import { api } from '../../services/api';
import { DrainControlState } from '../../services/steeringService';
import SessionControls from './SessionControls';

interface DrainState {
  enabled: boolean;
  intervalSec: number;
  pinned: boolean;
  primarySources: string[];
  availableSources: string[];
  lastDrainAt: string | null;
  nextDrainAt: string | null;
  failures: number;
  intentGoal: 'off' | 'shadow';
  intentGoalAvailable: boolean;
  buildMode: boolean;
  control: DrainControlState | null;
  breakUntil: string | null;
  epoch: number;
  forceNext: boolean;
  contextPack: { version: number; phase: { state?: string; blockTitle?: string } | null; summary: string } | null;
}

const INTERVALS = [60, 120, 180, 300];
const SOURCE_LABELS: Record<string, string> = {
  browser: 'Browser mic',
  'studio-mic': 'Studio mics',
  teams: 'Teams',
  vexa: 'Vexa',
};

const time = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '–');

interface Props {
  sessionId: string;
  canEdit: boolean;
}

/** Transcript drain switch and settings for a live session (agentic core R0). */
const TranscriptDrainControl: React.FC<Props> = ({ sessionId, canEdit }) => {
  const [state, setState] = useState<DrainState | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/sessions/${sessionId}/drain`);
      setState(res.data);
    } catch {
      /* the pill stays hidden until the state loads */
    }
  }, [sessionId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30_000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const update = async (body: Partial<Pick<DrainState, 'enabled' | 'intervalSec' | 'pinned' | 'primarySources' | 'intentGoal' | 'buildMode'>>) => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/sessions/${sessionId}/drain`, body);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not update the drain');
    } finally {
      setSaving(false);
    }
  };

  if (!state) return null;

  const toggleSource = (source: string) => {
    const next = state.primarySources.includes(source)
      ? state.primarySources.filter((s) => s !== source)
      : [...state.primarySources, source];
    if (next.length) update({ primarySources: next });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
          state.enabled
            ? state.failures > 0
              ? 'bg-amber-50 text-amber-700 border-amber-200'
              : 'bg-primary/10 text-primary border-primary/30'
            : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
        }`}
        title="Transcript drain"
      >
        <span className={`h-1.5 w-1.5 rounded-full ${state.enabled ? (state.failures > 0 ? 'bg-amber-500' : 'bg-primary') : 'bg-slate-300'}`} />
        {state.enabled ? `Drain ${Math.round(state.intervalSec / 60)}m` : 'Drain off'}
      </button>

      {open && (
        <div className="absolute right-0 top-8 z-30 w-96 bg-white border border-[#e2e8f0] rounded-xl shadow-lg p-4 flex flex-col gap-4 text-left">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-navy">Transcript drain</p>
              <p className="text-xs text-slate-400">Cleans the transcript and keeps the session record current</p>
            </div>
            <button
              type="button"
              disabled={!canEdit || saving}
              onClick={() => update({ enabled: !state.enabled })}
              className={`w-9 h-5 rounded-full transition-colors shrink-0 disabled:opacity-50 ${state.enabled ? 'bg-primary' : 'bg-slate-300'}`}
              aria-label={state.enabled ? 'Turn the drain off' : 'Turn the drain on'}
            >
              <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${state.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold text-primary tracking-widest uppercase">Interval</span>
              <button
                type="button"
                disabled={!canEdit || saving}
                onClick={() => update({ pinned: !state.pinned })}
                className={`flex items-center gap-1 text-xs font-semibold disabled:opacity-50 ${state.pinned ? 'text-navy' : 'text-slate-400'}`}
                title="Pinned: the drain keeps this interval instead of pacing itself"
              >
                <Pin size={12} /> {state.pinned ? 'Pinned' : 'Auto-pace'}
              </button>
            </div>
            <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
              {INTERVALS.map((sec) => (
                <button
                  key={sec}
                  type="button"
                  disabled={!canEdit || saving}
                  onClick={() => update({ intervalSec: sec })}
                  className={`flex-1 py-1.5 text-xs font-semibold transition-all disabled:opacity-60 ${
                    state.intervalSec === sec ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {sec / 60} min
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="text-[11px] font-bold text-primary tracking-widest uppercase">Room speech from</span>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {state.availableSources.map((source) => {
                const on = state.primarySources.includes(source);
                return (
                  <button
                    key={source}
                    type="button"
                    disabled={!canEdit || saving}
                    onClick={() => toggleSource(source)}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border disabled:opacity-60 ${
                      on ? 'bg-primary/10 text-primary border-primary/30' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {SOURCE_LABELS[source] ?? source}
                  </button>
                );
              })}
            </div>
            {!state.primarySources.length && <p className="mt-2 text-xs text-slate-400">Set automatically from the first source that sends.</p>}
          </div>

          {state.intentGoalAvailable && (
            <div className="flex items-center justify-between gap-3">
              <div>
                <span className="text-[11px] font-bold text-primary tracking-widest uppercase">Agentic intents</span>
                <p className="text-xs text-slate-400">Shadow: logged beside n8n after each drain, nothing fires.</p>
              </div>
              <button
                type="button"
                disabled={!canEdit || saving || !state.enabled}
                onClick={() => update({ intentGoal: state.intentGoal === 'shadow' ? 'off' : 'shadow' })}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border disabled:opacity-60 ${
                  state.intentGoal === 'shadow' ? 'bg-primary/10 text-primary border-primary/30' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {state.intentGoal === 'shadow' ? 'Shadow' : 'Off'}
              </button>
            </div>
          )}

          {state.intentGoalAvailable && state.intentGoal === 'shadow' && (
            <div className="flex items-center justify-between gap-3">
              <div>
                <span className="text-[11px] font-bold text-primary tracking-widest uppercase">Build loop</span>
                <p className="text-xs text-slate-400">On: requirements the room agrees become build hints, not only prototype feedback.</p>
              </div>
              <button
                type="button"
                disabled={!canEdit || saving || !state.enabled}
                onClick={() => update({ buildMode: !state.buildMode })}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border disabled:opacity-60 ${
                  state.buildMode ? 'bg-primary/10 text-primary border-primary/30' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {state.buildMode ? 'On' : 'Off'}
              </button>
            </div>
          )}

          <div className="border-t border-[#e2e8f0] pt-3">
            <SessionControls sessionId={sessionId} canEdit={canEdit} source="web" drain={state} onChanged={load} />
          </div>

          <div className="border-t border-[#e2e8f0] pt-3 flex flex-col gap-1.5">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Last {time(state.lastDrainAt)} · next {state.enabled ? time(state.nextDrainAt) : '–'}</span>
              <button type="button" onClick={load} className="text-slate-400 hover:text-primary" aria-label="Refresh">
                <RefreshCw size={12} />
              </button>
            </div>
            {state.failures > 0 && <p className="text-xs text-amber-600">Last {state.failures} drain{state.failures > 1 ? 's' : ''} failed; retrying.</p>}
            {state.contextPack?.phase?.blockTitle && (
              <p className="text-xs text-slate-600">
                <span className="font-semibold text-navy">Now:</span> {state.contextPack.phase.blockTitle}
              </p>
            )}
            {state.contextPack?.summary && <p className="text-xs text-slate-500 line-clamp-4">{state.contextPack.summary}</p>}
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default TranscriptDrainControl;
